'use client';

import { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

import { useAgentBlock } from '@/app/(app)/admin/agent/hooks';
import { Editor } from '@/components/editor';
import { useAuthCtx } from '@/components/providers/auth-provider';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';

import { useVoiceAgentCtx } from './provider';

export function VoiceAgentBlocks() {
  const { agent, update } = useVoiceAgentCtx();

  return (
    <Card>
      <CardHeader>
        <div className='flex flex-row items-center justify-between'>
          <CardTitle>Blocks ({agent.blocks.length})</CardTitle>
          <div className='ml-auto'>
            <VoiceAgentPromptPreview agentId={agent.id} />
          </div>
        </div>
      </CardHeader>
      <CardContent className='flex flex-col gap-2'>
        {agent.blocks.map((blockSetting, index) => (
          <BlockSettingCard
            key={blockSetting.block_id}
            blockSetting={blockSetting}
            onUpdate={(updated) => {
              const newBlocks = [...agent.blocks];
              newBlocks[index] = updated;
              update({ blocks: newBlocks });
            }}
          />
        ))}
      </CardContent>
    </Card>
  );
}

interface PromptPreviewProps {
  agentId: string;
}

export function VoiceAgentPromptPreview({ agentId }: PromptPreviewProps) {
  const { isSuperuser } = useAuthCtx();
  const { api } = useApiV1Ctx();
  const { data, error, refetch } = api.agentVoice.buildVoiceAgent.useQuery(
    {
      path: { id: agentId },
    },
    {
      enabled: isSuperuser,
    }
  );

  if (error) {
    return <Badge variant='destructive'>Can&apos;t build prompt</Badge>;
  }

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button size='sm' variant='outline' className='cursor-pointer'>
          Preview Prompt
        </Button>
      </SheetTrigger>
      <SheetContent
        showClose
        className='w-full gap-2 overflow-y-auto pt-2 sm:max-w-3xl md:top-4 md:right-4 md:h-[calc(100%-2rem)] md:rounded-lg'
      >
        <SheetHeader>
          <SheetTitle>Prompt Compiled</SheetTitle>
          <SheetDescription>
            Compiled prompt, with build time variables used. Dynamic variables still required.
          </SheetDescription>
        </SheetHeader>
        <div className='space-y-3 px-4'>
          <Button variant='outline' onClick={() => refetch()}>
            Refetch
          </Button>
          <div className='bg-muted/50 rounded-lg border px-4 py-2 text-sm whitespace-pre-wrap'>{data?.prompt}</div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

type VoiceAgentBlockSettings = ApiSchemas['VoiceAgentBlockBase'];

interface BlockSettingCardProps {
  blockSetting: VoiceAgentBlockSettings;
  onUpdate: (updated: VoiceAgentBlockSettings) => void;
}

function BlockSettingCard({ blockSetting, onUpdate }: BlockSettingCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: block } = useAgentBlock(blockSetting.block_id);

  if (!block) {
    return (
      <div className='bg-muted/50 rounded-lg border px-4 py-2'>
        <div className='text-muted-foreground text-sm'>Block #{blockSetting.block_id} not found</div>
      </div>
    );
  }

  return (
    <div className='bg-muted/50 space-y-2 rounded-lg border px-4 py-2'>
      <div className='flex flex-row items-center'>
        <div className='flex flex-row items-center gap-3'>
          <Switch
            checked={blockSetting.is_enabled}
            onCheckedChange={(is_enabled) => onUpdate({ ...blockSetting, is_enabled })}
          />
          <div className='text-sm'>
            <span className='font-medium'>{block.name}</span>
            {!block.is_valid && (
              <Badge variant='destructive' className='ml-2 rounded-full'>
                Invalid Template
              </Badge>
            )}
          </div>
        </div>
        <div className='ml-auto flex gap-2'>
          <Button variant='outline' size='icon' onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? <ChevronUpIcon className='size-4' /> : <ChevronDownIcon className='size-4' />}
          </Button>
        </div>
      </div>
      {isExpanded && (
        <div className='w-full pb-2'>
          <Editor
            content={block.template}
            className='bg-background h-80 rounded-lg border p-2'
            language='markdown'
            lineNumbers='on'
          />
        </div>
      )}
    </div>
  );
}
